import { jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useMemo, useRef } from 'react';
import semverCoerce from 'semver/functions/coerce';
import semverLt from 'semver/functions/lt';
import { styled } from 'styled-components';
import { useIntersectionObserver } from '@hivebrite/react-hooks';
import { Box } from "../../../box/src";
import { useEmojiPickerContext } from '../context/EmojiPickerContext';
import { useEmojiIconKeyboardNavigation } from '../hooks/useEmojiIconKeyboardNavigation';
export const EmojiIconList = () => {
    const { emojiData } = useEmojiPickerContext();
    const emojiIconListRef = useRef();
    const { handleKeydown: handleListKeydown } = useEmojiIconKeyboardNavigation();
    const handleListFocus = (e) => {
        e.preventDefault();
    };
    return (_jsx(Box, { display: "block", flexDirection: "column", fontFamily: "Segoe UI Emoji", maxHeight: 236, onFocus: handleListFocus, onKeyDown: handleListKeydown, overflowX: "hidden", overflowY: "auto", px: 2, ref: emojiIconListRef, tabIndex: 0, children: Object.entries(emojiData).map(([groupName, groupEmojis]) => (_jsx(EmojiData, { groupEmojis: groupEmojis, groupName: groupName }, groupName))) }));
};
const EmojiData = ({ groupEmojis, groupName, }) => {
    const { addEmojiGroupWrapperRef, maxVersion, onEmojiPick, setFocusedEmoji, setPreviewEmoji, setSelectedEmojiGroup, } = useEmojiPickerContext();
    const emojiGroupWrapperRef = useRef();
    useEffect(() => {
        if (emojiGroupWrapperRef.current) {
            addEmojiGroupWrapperRef(groupName, emojiGroupWrapperRef);
        }
    }, [addEmojiGroupWrapperRef, emojiGroupWrapperRef, groupName]);
    return useMemo(() => (_jsx(EmojiGroupWrapper, { groupEmojis: groupEmojis, groupName: groupName, maxVersion: maxVersion, onEmojiPick: onEmojiPick, ref: emojiGroupWrapperRef, setFocusedEmoji: setFocusedEmoji, setPreviewEmoji: setPreviewEmoji, setSelectedEmojiGroup: setSelectedEmojiGroup }, groupName)), [
        groupEmojis,
        groupName,
        maxVersion,
        onEmojiPick,
        setFocusedEmoji,
        setPreviewEmoji,
        setSelectedEmojiGroup,
    ]);
};
const EmojiGroupWrapper = forwardRef(({ groupEmojis, groupName, maxVersion, onEmojiPick, setFocusedEmoji, setPreviewEmoji, setSelectedEmojiGroup, }, ref) => {
    const handleIntersectionObserver = ([entry]) => {
        if (entry.isIntersecting && entry.intersectionRatio > 0) {
            setSelectedEmojiGroup(groupName);
        }
    };
    useIntersectionObserver(ref, handleIntersectionObserver);
    return (_jsx(Box, { flexWrap: "wrap", pt: 2, ref: ref, children: groupEmojis.map((emoji) => (_jsx(EmojiIcon, { emoji: emoji, maxVersion: maxVersion, onEmojiPick: onEmojiPick, setFocusedEmoji: setFocusedEmoji, setPreviewEmoji: setPreviewEmoji }, emoji.emoji))) }));
});
const EmojiIcon = ({ emoji, maxVersion, onEmojiPick, setFocusedEmoji, setPreviewEmoji, }) => {
    const emojiRef = useRef(null);
    if (maxVersion &&
        semverLt(semverCoerce(maxVersion), semverCoerce(emoji.unicode_version))) {
        return null;
    }
    const handleEmojiMouseEnter = () => {
        setPreviewEmoji(emoji);
    };
    const handleEmojiClick = () => {
        onEmojiPick(emoji);
    };
    const handleEmojiKeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleEmojiClick();
        }
    };
    const handleEmojiFocus = () => {
        setPreviewEmoji(emoji);
        setFocusedEmoji(emojiRef);
    };
    return (_jsx(Icon, { onClick: handleEmojiClick, onFocus: handleEmojiFocus, onKeyDown: handleEmojiKeydown, onMouseEnter: handleEmojiMouseEnter, ref: emojiRef, role: "button", tabIndex: 0, title: emoji.name, children: emoji.emoji }));
};
const Icon = styled(Box).attrs(() => ({
    alignItems: 'center',
    fontSize: 24,
    height: 32,
    justifyContent: 'center',
    mb: 4,
    mx: 1,
    width: 32,
})) `
  cursor: pointer;
  border-radius: 4px;

  &:hover,
  &:focus {
    background-color: ${(props) => props.theme.colors.primaryDark};
  }
`;
//# sourceMappingURL=EmojiIconList.js.map